<template>
  <v-form class="my-2"
    v-model="detailMixin.valid"
    ref="formNutsvoorzieningComfort">
    <div
      v-if="detailMixin.element && detailMixin.element.multicheck && detailMixin.element.fields"
      :class="DetailMixin_Dirty ? 'dirtyForm' : DetailMixin_Revertable && !detailMixin.element.isNew ? 'editForm' : ''"
      >
      <h2 class="primary--text">Beschrijving van nutsvoorzieningen en comfort</h2>

      <div v-if="selectieControlTypes && selectieControlTypes.length">
        <control-multi-check :cols="3" :list="nutsvoorzieningTypes" v-model="detailMixin.element.multicheck[selectieControlTypes[0].idCode]" :selectieControlTypes="selectieControlTypes" :title="selectieControlTypes[0].langeBeschrijving"/>
        <v-divider></v-divider>
        <control-multi-check :cols="3" :list="verwarmingssysteemTypes" v-model="detailMixin.element.multicheck[selectieControlTypes[1].idCode]" :selectieControlTypes="selectieControlTypes" :title="selectieControlTypes[1].langeBeschrijving"/>
        <v-divider></v-divider>
        <control-multi-check :cols="3" :list="comfortTypes" v-model="detailMixin.element.multicheck[selectieControlTypes[2].idCode]" :selectieControlTypes="selectieControlTypes" :title="selectieControlTypes[2].langeBeschrijving"/>

        <!-- EXTRA INFORMATIE ZONNEPANELEN -->
        <div v-show="selectedComfortDescriptions.includes('Zonnepanelen')" class="ml-4">
          <h4 class="secondary--text">Extra informatie zonnepanelen</h4>
          <v-row>
            <v-col cols="2" md="1">
              <v-text-field label="Aantal" v-model="detailMixin.element.fields.zonnepanelenAantal" type="number" @keypress="validateNumericInput"/>
            </v-col>
            <v-col cols="3" md="2">
              <v-text-field label="Totaal vermogen" v-model="detailMixin.element.fields.zonnepanelenTotaalVermogen"/>
            </v-col>
          </v-row>
        </div>
      </div>
      <v-divider class="mb-5"></v-divider>

      <h3 class="secondary--text my-3">Sanitair</h3>
      <v-row>
        <v-col cols="12" md="9">
          <v-row class="pb-0">
              <v-col cols="12" md="6">
                <v-textarea label="Badkamer" auto-grow outlined v-model="detailMixin.element.fields.sanitairBadkamerBeschrijving"/>
              </v-col>
              <v-col cols="12" md="6">
                <v-textarea label="Keuken" auto-grow outlined v-model="detailMixin.element.fields.sanitairKeukenBeschrijving"/>
              </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-divider class="my-5"></v-divider>

      <h3 class="secondary--text my-3">Bijlage</h3>
      <v-row class="pb-0">
        <v-col cols="12" md="6">
          <control-file-input-drag-and-drop v-model="detailMixin.element.files" @download="Download($event)" :accept="['.png', '.jpg', '.jpeg', '.gif']"/>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<script>
import ControlFileInputDragAndDrop from '../ControlFileInputDragAndDrop.vue'
import ControlMultiCheck from '../ControlMultiCheck.vue'
import codeService from '../../services/codeService'
import schattingService from '../../services/schattingService'
import errorMixin from '@/mixins/errorMixin'
import commonMixin from '@/mixins/commonMixin'
import detailMixin from '../../mixins/detailMixin'
import blobMixin from '../../mixins/blobMixin'
import offlineStorage from '@/utils/offlineStorage'
import networkStatus from '@/utils/networkStatus'
import { debounce } from 'lodash'

export default {
  components: {
    ControlFileInputDragAndDrop,
    ControlMultiCheck
  },
  mixins: [
    errorMixin,
    commonMixin,
    detailMixin,
    blobMixin
  ],
  mounted () {
    codeService.Init(this)
    schattingService.Init(this)
    this.initAutoSave()
  },
  props: {
    idSchatting: {
      default: null,
      type: String
    },
    visible: Boolean
  },
  data () {
    return {
      referentieType: 'c427744c-1ff2-41f8-8667-72c6daec33f2',
      nutsvoorzieningTypes: null,
      verwarmingssysteemTypes: null,
      comfortTypes: null,
      selectieControlTypes: null,
      extraBeschrijving: null
    }
  },
  watch: {
    async visible () {
      if (this.visible) {
        await this.SetVariables()
        await this.loadEverything()
        await this.SetSelectionList()
      } else if (this.DetailMixin_Dirty) {
        this.DetailMixin_Save()
      }
    },
    // Deep watch for automatic caching of afmetingen data
    'detailMixin.element.multicheck': {
      deep: true,
      handler: function (newVal, oldVal) {
        // Only auto-save if we have data and it's not the initial load
        if (newVal && oldVal && this.idSchatting) {
          this.debouncedAutoSave()
        }
      }
    },
    'detailMixin.element.fields': {
      deep: true,
      handler: function (newVal, oldVal) {
        // Only auto-save if we have data and it's not the initial load
        if (newVal && oldVal && this.idSchatting) {
          this.debouncedAutoSave()
        }
      }
    }
  },
  computed: {
    selectedComfortDescriptions () {
      if (!this.detailMixin.element ||
          !this.detailMixin.element.multicheck ||
          !this.selectieControlTypes ||
          this.selectieControlTypes.length < 3 ||
          !this.comfortTypes) {
        return []
      }

      const selectedIds = this.detailMixin.element.multicheck[this.selectieControlTypes[2].idCode] || []
      return this.comfortTypes
        .filter(item => selectedIds.some(selected => selected.idCode === item.idCode))
        .map(item => item.langeBeschrijving)
    }
  },
  methods: {
    initAutoSave () {
      // Create a debounced version of the auto-save function
      // This will wait 1 second after the last change before saving
      this.debouncedAutoSave = debounce(this.autoSaveToCache, 1000)
    },
    async autoSaveToCache () {
      const id = this.DetailMixin_GetId()
      if (!id || !this.detailMixin.element?.fields || !this.detailMixin.element?.multicheck) return

      try {
        await offlineStorage.saveSchattingStep('nutsvoorzieningComfort', id, this.detailMixin.element.fields)
        await offlineStorage.saveSchattingStep('nutsvoorzieningComfortCodeSelecties', id, this.detailMixin.element.multicheck)
        this.CommonMixin_Snackbar('Aanpassing lokaal opgeslagen', 'success')
      } catch (error) {
        console.error('Auto-save to cache failed:', error)
        this.CommonMixin_Snackbar('Automatisch opslaan mislukt', 'error')

        // Clear the error message after 3 seconds
        setTimeout(() => {
          if (this.autoSaveStatus === 'error') {
            this.autoSaveStatus = null
            this.autoSaveMessage = ''
          }
        }, 3000)
      }
    },
    validateNumericInput (event) {
      const charCode = event.keyCode || event.which
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault()
      }
    },
    Download (blob) {
      const blobFormatted = this.CommonMixin_Clone(blob)
      blobFormatted.bestandsnaam = blob.name
      this.BlobMixin_Download(blobFormatted)
    },
    async loadEverything () {
      this.CommonMixin_Load(this.$options.name, true)
      await this.SetNutsvoorzieningTypes()
      await this.SetVerwarmingssysteemTypes()
      await this.SetComfortTypes()
      await this.SetSelectieControlTypes()
      this.CommonMixin_Load(this.$options.name, false)
    },
    async SetNutsvoorzieningTypes () {
      const cachedNutsvoorzieningTypes = await offlineStorage.getLookupData('nutsvoorzieningTypes')
      if (cachedNutsvoorzieningTypes) {
        this.nutsvoorzieningTypes = cachedNutsvoorzieningTypes
      }
    },
    async SetVerwarmingssysteemTypes () {
      const cachedVerwarmingssysteemTypes = await offlineStorage.getLookupData('verwarmingssysteem')
      if (cachedVerwarmingssysteemTypes) {
        this.verwarmingssysteemTypes = cachedVerwarmingssysteemTypes
      }
    },
    async SetComfortTypes () {
      const cachedComfortTypes = await offlineStorage.getLookupData('comfortTypes')
      if (cachedComfortTypes) {
        this.comfortTypes = cachedComfortTypes
      }
    },
    async SetSelectionList () {
      const id = this.DetailMixin_GetId()

      if (id) {
        const responseNutsvoorzieningComfortMulticheck = await offlineStorage.getSchattingStep('nutsvoorzieningComfortCodeSelecties', id)
        var element = {
          multicheck: {},
          fields: {},
          files: []
        }

        if (responseNutsvoorzieningComfortMulticheck) {
          const { idSchatting, timestamp, syncStatus, ...multicheckData } = responseNutsvoorzieningComfortMulticheck
          element.multicheck = this.CommonMixin_Clone(multicheckData)
          this.$forceUpdate()
        }

        const responseNutsvoorzieningenComfort = await offlineStorage.getSchattingStep('nutsvoorzieningComfort', id)
        if (responseNutsvoorzieningenComfort) {
          element.fields = this.CommonMixin_Clone(responseNutsvoorzieningenComfort)
          this.$forceUpdate()
        }

        this.detailMixin.element = this.CommonMixin_Clone(element)

        if (networkStatus.isOnline()) {
          try {
            const files = await this.BlobMixin_GetFiles(this.idSchatting, this.referentieType)
            this.detailMixin.element.files = files
            this.$forceUpdate()
          } catch (error) {
            console.warn('Kon blobs niet ophalen:', error)
            if (!responseNutsvoorzieningenComfort) {
              console.error('Geen cached data beschikbaar voor afmetingen')
            }
          }
        }

        this.DetailMixin_InitDirtyWatch()
      }
    },
    async SetSelectieControlTypes () {
      const cachedNutsvoorzieningComfortControlTypes = await offlineStorage.getLookupData('selectieControlTypes')
      if (cachedNutsvoorzieningComfortControlTypes) {
        this.selectieControlTypes = cachedNutsvoorzieningComfortControlTypes.filter(x => x.waarde >= 500 && x.waarde <= 599)
      }
    },
    SetVariables () {
      this.detailMixin.key = 'id'
      this.detailMixin.rightsNeeded = ['admin', 'gebruiker']
      this.detailMixin.functions.validate = this.$refs.formNutsvoorzieningComfort.validate
      this.detailMixin.functions.save = this.save
    },
    async save (element) {
      if (!this.selectedComfortDescriptions.includes('Zonnepanelen')) {
        element.fields.zonnepanelenAantal = null
        element.fields.zonnepanelenTotaalVermogen = null
      }
      var multicheck = await codeService.SaveMulticheck({ IdReferentie: this.idSchatting, IdCodeReferentieType: this.referentieType, MultiSelects: element.multicheck })
      var fields = await schattingService.UpdateSchattingNutsvoorzieningComfort(element.fields)
      await this.BlobMixin_SaveFiles(element.files, this.idSchatting, this.referentieType)
      var multicheckData = null
      var fieldsData = null
      if (multicheck && multicheck.data) {
        multicheckData = multicheck.data
      }
      if (fields && fields.data) {
        fieldsData = fields.data
      }
      return {
        data: {
          multicheck: multicheckData,
          fields: fieldsData
        }
      }
    },
    Validate () {
      this.$refs.formNutsvoorzieningComfort.validate()
      return this.detailMixin.valid
    }
  }
}
</script>
